class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();

    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 200);

    const facetForm = this.querySelector('form');

    if (facetForm) {
      facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));
    }

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');

    if (facetWrapper) {
      facetWrapper.addEventListener('keyup', onKeyUpEscape);
    }
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state
        ? event.state.searchParams
        : FacetFiltersForm.searchParamsInitial;

      if (searchParams === FacetFiltersForm.searchParamsPrev) return;

      FacetFiltersForm.renderPage(searchParams, null, false);
    };

    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;

    const sections = FacetFiltersForm.getSections();

    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');

    const productGridContainer = document.getElementById('ProductGridContainer');

    if (productGridContainer) {
      const collection = productGridContainer.querySelector('.collection');

      if (collection) {
        collection.classList.add('loading');
      }
    }

    if (countContainer) {
      countContainer.classList.add('loading');
    }

    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;

      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) {
      FacetFiltersForm.updateURLHash(searchParams);
    }
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;

        FacetFiltersForm.filterData = [
          ...FacetFiltersForm.filterData,
          {
            html,
            url
          }
        ];

        FacetFiltersForm.renderFilters(html, event);

        FacetFiltersForm.renderProductGridContainer(html);

        FacetFiltersForm.renderProductCount(html);

        if (typeof initializeScrollAnimationTrigger === 'function') {
          initializeScrollAnimationTrigger(html);
        }
      })
      .catch((error) => {
        console.error('Facet filter fetch error:', error);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const cachedData = FacetFiltersForm.filterData.find(filterDataUrl);

    if (!cachedData) return;

    const html = cachedData.html;

    FacetFiltersForm.renderFilters(html, event);

    FacetFiltersForm.renderProductGridContainer(html);

    FacetFiltersForm.renderProductCount(html);

    if (typeof initializeScrollAnimationTrigger === 'function') {
      initializeScrollAnimationTrigger(html);
    }
  }

  static renderProductGridContainer(html) {
    const productGridContainer = document.getElementById('ProductGridContainer');

    if (!productGridContainer) return;

    const parsedHTML = new DOMParser()
      .parseFromString(html, 'text/html');

    const newProductGridContainer =
      parsedHTML.getElementById('ProductGridContainer');

    if (!newProductGridContainer) return;

    productGridContainer.innerHTML =
      newProductGridContainer.innerHTML;

    productGridContainer
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  /*
   * ============================================================
   * UPDATE PRODUCT COUNTS
   * ============================================================
   *
   * This updates:
   *
   * 1. Shopify default product count
   * 2. Desktop product count
   * 3. Custom Ekaya count:
   *
   *    .main-prod-count-total
   *
   * Example:
   *
   * Before:
   * 570 Items
   *
   * After selecting:
   * color : Firozi
   *
   * It becomes:
   * 1 Item
   *
   * No page reload required.
   */

  static renderProductCount(html) {
    const parsedHTML = new DOMParser()
      .parseFromString(html, 'text/html');

    /*
     * ----------------------------------------------------------
     * Shopify Product Count
     * ----------------------------------------------------------
     */

    const sourceProductCount =
      parsedHTML.getElementById('ProductCount');

    const currentProductCount =
      document.getElementById('ProductCount');

    const currentProductCountDesktop =
      document.getElementById('ProductCountDesktop');

    if (sourceProductCount) {
      const count = sourceProductCount.innerHTML;

      if (currentProductCount) {
        currentProductCount.innerHTML = count;
        currentProductCount.classList.remove('loading');
      }

      if (currentProductCountDesktop) {
        currentProductCountDesktop.innerHTML = count;
        currentProductCountDesktop.classList.remove('loading');
      }
    }

    /*
     * ----------------------------------------------------------
     * CUSTOM EKAYA PRODUCT COUNT
     * ----------------------------------------------------------
     *
     * Your actual HTML is:
     *
     * <p class="main-prod-count-total">
     *   570 Items
     * </p>
     *
     * We first try to get the count from Shopify's
     * data-product-count attribute.
     *
     * If that is not available, we use the
     * server-rendered .main-prod-count-total from
     * the AJAX response.
     */

    const customProductCount =
      document.querySelector('.main-prod-count-total');

    if (!customProductCount) return;

    let productCount = null;

    /*
     * Method 1:
     * Read data-product-count from Shopify ProductCount
     */

    if (
      sourceProductCount &&
      sourceProductCount.dataset &&
      sourceProductCount.dataset.productCount !== undefined
    ) {
      productCount =
        parseInt(
          sourceProductCount.dataset.productCount,
          10
        );
    }

    /*
     * Method 2:
     * Read the server-rendered custom count
     */

    if (
      (productCount === null || isNaN(productCount))
    ) {
      const sourceCustomProductCount =
        parsedHTML.querySelector('.main-prod-count-total');

      if (sourceCustomProductCount) {
        const text =
          sourceCustomProductCount.textContent.trim();

        const numberMatch =
          text.match(/[\d,]+/);

        if (numberMatch) {
          productCount =
            parseInt(
              numberMatch[0].replace(/,/g, ''),
              10
            );
        }
      }
    }

    /*
     * Method 3:
     * Read the first number from Shopify ProductCount
     *
     * Example:
     *
     * "1 of 570 products"
     *
     * We take:
     *
     * 1
     */

    if (
      productCount === null ||
      isNaN(productCount)
    ) {
      if (sourceProductCount) {
        const text =
          sourceProductCount.textContent.trim();

        const numberMatch =
          text.match(/[\d,]+/);

        if (numberMatch) {
          productCount =
            parseInt(
              numberMatch[0].replace(/,/g, ''),
              10
            );
        }
      }
    }

    /*
     * ----------------------------------------------------------
     * Update Custom Count
     * ----------------------------------------------------------
     */

    if (
      productCount !== null &&
      !isNaN(productCount)
    ) {
      customProductCount.textContent =
        `${productCount.toLocaleString('en-IN')} ${
          productCount === 1 ? 'Item' : 'Items'
        }`;
    }
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser()
      .parseFromString(html, 'text/html');

    const facetDetailsElements =
      parsedHTML.querySelectorAll(
        '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
      );

    const matchesIndex = (element) => {
      const jsFilter =
        event && event.target
          ? event.target.closest('.js-filter')
          : undefined;

      return jsFilter
        ? element.dataset.index === jsFilter.dataset.index
        : false;
    };

    const facetsToRender =
      Array.from(facetDetailsElements)
        .filter(
          (element) => !matchesIndex(element)
        );

    const countsToRender =
      Array.from(facetDetailsElements)
        .find(matchesIndex);

    facetsToRender.forEach((element) => {
      const target =
        document.querySelector(
          `.js-filter[data-index="${element.dataset.index}"]`
        );

      if (target) {
        target.innerHTML = element.innerHTML;
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);

    FacetFiltersForm.renderAdditionalElements(
      parsedHTML
    );

    if (
      countsToRender &&
      event &&
      event.target
    ) {
      const targetFilter =
        event.target.closest('.js-filter');

      if (targetFilter) {
        FacetFiltersForm.renderCounts(
          countsToRender,
          targetFilter
        );
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = [
      '.active-facets-mobile',
      '.active-facets-desktop'
    ];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement =
        html.querySelector(selector);

      if (!activeFacetsElement) return;

      const currentElement =
        document.querySelector(selector);

      if (currentElement) {
        currentElement.innerHTML =
          activeFacetsElement.innerHTML;
      }
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = [
      '.mobile-facets__open',
      '.mobile-facets__count',
      '.sorting'
    ];

    mobileElementSelectors.forEach((selector) => {
      const sourceElement =
        html.querySelector(selector);

      const targetElement =
        document.querySelector(selector);

      if (
        !sourceElement ||
        !targetElement
      ) {
        return;
      }

      targetElement.innerHTML =
        sourceElement.innerHTML;
    });

    const mobileFacetForm =
      document.getElementById(
        'FacetFiltersFormMobile'
      );

    if (
      mobileFacetForm &&
      mobileFacetForm.closest('menu-drawer')
    ) {
      mobileFacetForm
        .closest('menu-drawer')
        .bindEvents();
    }
  }

  static renderCounts(source, target) {
    if (!source || !target) return;

    const targetElement =
      target.querySelector(
        '.facets__selected'
      );

    const sourceElement =
      source.querySelector(
        '.facets__selected'
      );

    const targetElementAccessibility =
      target.querySelector(
        '.facets__summary'
      );

    const sourceElementAccessibility =
      source.querySelector(
        '.facets__summary'
      );

    if (
      sourceElement &&
      targetElement
    ) {
      targetElement.outerHTML =
        sourceElement.outerHTML;
    }

    if (
      targetElementAccessibility &&
      sourceElementAccessibility
    ) {
      targetElementAccessibility.outerHTML =
        sourceElementAccessibility.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState(
      {
        searchParams
      },
      '',
      `${window.location.pathname}${
        searchParams
          ? '?' + searchParams
          : ''
      }`
    );
  }

  static getSections() {
    const productGrid =
      document.getElementById(
        'product-grid'
      );

    if (!productGrid) {
      return [];
    }

    return [
      {
        section:
          productGrid.dataset.id
      }
    ];
  }

  createSearchParams(form) {
    const formData =
      new FormData(form);

    return new URLSearchParams(
      formData
    ).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(
      searchParams,
      event
    );
  }

  onSubmitHandler(event) {
    event.preventDefault();

    const sortFilterForms =
      document.querySelectorAll(
        'facet-filters-form form'
      );

    /*
     * Mobile checkbox
     *
     * Keep the current behavior so that
     * your existing Apply button flow
     * continues working.
     */

    if (
      event.srcElement &&
      event.srcElement.className ===
        'mobile-facets__checkbox'
    ) {
      return;
    }

    const forms = [];

    const closestForm =
      event.target.closest('form');

    const isMobile =
      closestForm &&
      closestForm.id ===
        'FacetFiltersFormMobile';

    sortFilterForms.forEach((form) => {
      if (!isMobile) {
        if (
          form.id === 'FacetSortForm' ||
          form.id === 'FacetFiltersForm' ||
          form.id ===
            'FacetSortDrawerForm'
        ) {
          const noJsElements =
            document.querySelectorAll(
              '.no-js-list'
            );

          noJsElements.forEach((el) => {
            el.remove();
          });

          forms.push(
            this.createSearchParams(form)
          );
        }
      } else if (
        form.id ===
        'FacetFiltersFormMobile'
      ) {
        forms.push(
          this.createSearchParams(form)
        );
      }
    });

    this.onSubmitForm(
      forms.join('&'),
      event
    );
  }

  onActiveFilterClick(event) {
    event.preventDefault();

    FacetFiltersForm.toggleActiveFacets();

    const url =
      event.currentTarget.href.indexOf('?') ===
      -1
        ? ''
        : event.currentTarget.href.slice(
            event.currentTarget.href.indexOf('?') +
              1
          );

    FacetFiltersForm.renderPage(
      url
    );
  }
}


/*
 * ============================================================
 * CUSTOM SORT
 * ============================================================
 */

$(document).on(
  'click',
  '.sort-by-ul-popup li',
  function (event) {
    const sortParam =
      'sort_by=' +
      $(this).attr('data-val');

    const sortFilterForms =
      document.querySelectorAll(
        'facet-filters-form form'
      );

    const forms = [];

    const closestForm =
      event.target.closest('form');

    const isMobile =
      closestForm &&
      closestForm.id ===
        'FacetFiltersFormMobile';

    sortFilterForms.forEach((form) => {
      if (!isMobile) {
        if (
          form.id === 'FacetSortForm' ||
          form.id ===
            'FacetFiltersForm' ||
          form.id ===
            'FacetSortDrawerForm'
        ) {
          const noJsElements =
            document.querySelectorAll(
              '.no-js-list'
            );

          noJsElements.forEach((el) => {
            el.remove();
          });

          forms.push(sortParam);
        }
      } else if (
        form.id ===
        'FacetFiltersFormMobile'
      ) {
        forms.push(sortParam);
      }
    });

    const facetForm =
      document.querySelector(
        'facet-filters-form'
      );

    if (facetForm) {
      facetForm.onSubmitForm(
        forms.join('&'),
        event
      );
    }
  }
);


/*
 * ============================================================
 * INITIALIZE FACET FILTER
 * ============================================================
 */

FacetFiltersForm.filterData = [];

FacetFiltersForm.searchParamsInitial =
  window.location.search.slice(1);

FacetFiltersForm.searchParamsPrev =
  window.location.search.slice(1);

customElements.define(
  'facet-filters-form',
  FacetFiltersForm
);

FacetFiltersForm.setListeners();


/*
 * ============================================================
 * PRICE RANGE
 * ============================================================
 */

class PriceRange extends HTMLElement {
  constructor() {
    super();

    this.querySelectorAll('input').forEach(
      (element) =>
        element.addEventListener(
          'change',
          this.onRangeChange.bind(this)
        )
    );

    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(
      event.currentTarget
    );

    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const inputs =
      this.querySelectorAll('input');

    const minInput = inputs[0];
    const maxInput = inputs[1];

    if (!minInput || !maxInput) {
      return;
    }

    if (maxInput.value) {
      minInput.setAttribute(
        'max',
        maxInput.value
      );
    }

    if (minInput.value) {
      maxInput.setAttribute(
        'min',
        minInput.value
      );
    }

    if (minInput.value === '') {
      maxInput.setAttribute(
        'min',
        0
      );
    }

    if (maxInput.value === '') {
      minInput.setAttribute(
        'max',
        maxInput.getAttribute('max')
      );
    }
  }

  adjustToValidValues(input) {
    const value =
      Number(input.value);

    const min =
      Number(input.getAttribute('min'));

    const max =
      Number(input.getAttribute('max'));

    if (value < min) {
      input.value = min;
    }

    if (value > max) {
      input.value = max;
    }
  }
}

customElements.define(
  'price-range',
  PriceRange
);


/*
 * ============================================================
 * FACET REMOVE
 * ============================================================
 */

class FacetRemove extends HTMLElement {
  constructor() {
    super();

    const facetLink =
      this.querySelector('a');

    if (!facetLink) return;

    facetLink.setAttribute(
      'role',
      'button'
    );

    facetLink.addEventListener(
      'click',
      this.closeFilter.bind(this)
    );

    facetLink.addEventListener(
      'keyup',
      (event) => {
        event.preventDefault();

        if (
          event.code.toUpperCase() ===
          'SPACE'
        ) {
          this.closeFilter(event);
        }
      }
    );
  }

  closeFilter(event) {
    event.preventDefault();

    const form =
      this.closest(
        'facet-filters-form'
      ) ||
      document.querySelector(
        'facet-filters-form'
      );

    if (form) {
      form.onActiveFilterClick(
        event
      );
    }
  }
}

customElements.define(
  'facet-remove',
  FacetRemove
);